import type { SortingState } from '@tanstack/react-table';
import moment from 'moment-timezone';

import { downloadFile } from 'utility/browser';

import {
    formatReportDisplayCellValue,
    formatReportExportCellValue,
    getReportColumnById,
    getReportColumnFooterAggregate,
    parseReportDateTime,
    type ReportColumnDefinition,
} from './reportTableTypes';

export type ReportExportFormat = 'csv' | 'xlsx' | 'pdf';

export type ReportExportOptions<TRow extends Record<string, unknown>> = {
    rows: TRow[];
    columnDefinitions: ReportColumnDefinition<TRow>[];
    selectedColumnIds: string[];
    searchValue: string;
    sorting: SortingState;
    reportTitle: string;
    reportCategory: string;
};

export type ReportExportSheet = {
    headers: string[];
    rows: string[][];
    footerRow: string[] | null;
};

function parseReportDate(value: string | number | null): number {
    if (typeof value !== 'string') {
        return 0;
    }

    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
}

function getVisibleColumnDefs<TRow extends Record<string, unknown>>(
    columnDefinitions: ReportColumnDefinition<TRow>[],
    selectedColumnIds: string[],
): ReportColumnDefinition<TRow>[] {
    return selectedColumnIds
        .map((columnId) => getReportColumnById(columnDefinitions, columnId))
        .filter((def): def is ReportColumnDefinition<TRow> => def !== undefined);
}

function rowMatchesSearch<TRow extends Record<string, unknown>>(
    row: TRow,
    query: string,
    columnDefinitions: ReportColumnDefinition<TRow>[],
): boolean {
    const normalizedQuery = query.toLowerCase();
    if (!normalizedQuery) {
        return true;
    }

    const searchableValues = columnDefinitions.flatMap((def) => {
        const raw = def.getValue(row);
        const formatted = formatReportDisplayCellValue(def as ReportColumnDefinition<unknown>, raw);
        return [String(raw ?? ''), String(formatted)];
    });

    return searchableValues.some((value) => value.toLowerCase().includes(normalizedQuery));
}

function compareRows<TRow extends Record<string, unknown>>(
    rowA: TRow,
    rowB: TRow,
    columnId: string,
    desc: boolean,
    columnDefinitions: ReportColumnDefinition<TRow>[],
): number {
    const def = getReportColumnById(columnDefinitions, columnId);
    if (!def) {
        return 0;
    }

    const rawA = def.getValue(rowA);
    const rawB = def.getValue(rowB);

    let comparison = 0;

    if (def.sortType === 'datetime') {
        const a = parseReportDateTime(rawA);
        const b = parseReportDateTime(rawB);
        comparison = a === b ? 0 : a > b ? 1 : -1;
    } else if (def.sortType === 'date') {
        const a = parseReportDate(rawA);
        const b = parseReportDate(rawB);
        comparison = a === b ? 0 : a > b ? 1 : -1;
    } else if (typeof rawA === 'number' && typeof rawB === 'number') {
        comparison = rawA === rawB ? 0 : rawA > rawB ? 1 : -1;
    } else {
        comparison = String(rawA ?? '').localeCompare(String(rawB ?? ''), undefined, {
            numeric: true,
            sensitivity: 'base',
        });
    }

    return desc ? -comparison : comparison;
}

function sortRows<TRow extends Record<string, unknown>>(
    rows: TRow[],
    sorting: SortingState,
    columnDefinitions: ReportColumnDefinition<TRow>[],
): TRow[] {
    if (sorting.length === 0) {
        return rows;
    }

    return [...rows].sort((rowA, rowB) => {
        for (const sort of sorting) {
            const result = compareRows(rowA, rowB, sort.id, sort.desc ?? false, columnDefinitions);
            if (result !== 0) {
                return result;
            }
        }
        return 0;
    });
}

function buildFooterRow<TRow extends Record<string, unknown>>(
    visibleColumnDefs: ReportColumnDefinition<TRow>[],
    filteredRows: TRow[],
): string[] | null {
    const hasSummableColumns = visibleColumnDefs.some((def) => def.summable);
    if (!hasSummableColumns || filteredRows.length === 0) {
        return null;
    }

    const footerRow: string[] = [];
    let totalsLabelPlaced = false;

    visibleColumnDefs.forEach((def) => {
        if (!def.summable) {
            if (!totalsLabelPlaced) {
                footerRow.push('Totals');
                totalsLabelPlaced = true;
            } else {
                footerRow.push('');
            }
            return;
        }

        const aggregate = getReportColumnFooterAggregate(
            def as ReportColumnDefinition<unknown>,
            filteredRows,
        );
        const formatted =
            aggregate == null
                ? ''
                : String(
                      formatReportDisplayCellValue(
                          def as ReportColumnDefinition<unknown>,
                          aggregate,
                      ),
                  );
        footerRow.push(
            !formatted ? '' : def.format === 'percent' ? `avg ${formatted}` : `sum ${formatted}`,
        );
    });

    return footerRow;
}

export function buildReportExportSheet<TRow extends Record<string, unknown>>({
    rows,
    columnDefinitions,
    selectedColumnIds,
    searchValue,
    sorting,
}: Omit<ReportExportOptions<TRow>, 'reportTitle'>): ReportExportSheet {
    const visibleColumnDefs = getVisibleColumnDefs(columnDefinitions, selectedColumnIds);
    const filteredRows = rows.filter((row) =>
        rowMatchesSearch(row, searchValue, visibleColumnDefs),
    );
    const sortedRows = sortRows(filteredRows, sorting, columnDefinitions);

    const headers = visibleColumnDefs.map((def) => def.label);
    const exportRows = sortedRows.map((row) =>
        visibleColumnDefs.map((def) =>
            String(
                formatReportExportCellValue(
                    def as ReportColumnDefinition<unknown>,
                    def.getValue(row),
                ),
            ),
        ),
    );
    const footerRow = buildFooterRow(visibleColumnDefs, filteredRows);

    return {
        headers,
        rows: exportRows,
        footerRow,
    };
}

function escapeCsvCell(value: string): string {
    if (/[",\n\r]/.test(value)) {
        return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
}

function buildExportFilename(
    reportCategory: string,
    reportTitle: string,
    extension: string,
): string {
    const categoryPrefix: Partial<Record<string, string>> = {
        'Online Sales': 'online',
        'POS Sales': 'pos',
    };
    const slug = reportTitle
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

    const prefix = categoryPrefix[reportCategory];
    const filename = `${slug || 'report'}-${moment().format('YYYY-MM-DD-HHmm')}`;

    return `${prefix ? `${prefix}-` : ''}${filename}.${extension}`;
}

async function exportCsv(
    sheet: ReportExportSheet,
    reportTitle: string,
    reportCategory: string,
): Promise<void> {
    const lines = [
        sheet.headers.map(escapeCsvCell).join(','),
        ...sheet.rows.map((row) => row.map(escapeCsvCell).join(',')),
    ];

    if (sheet.footerRow) {
        lines.push(sheet.footerRow.map(escapeCsvCell).join(','));
    }

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    await downloadFile(buildExportFilename(reportCategory, reportTitle, 'csv'), blob);
}

async function exportXlsx(
    sheet: ReportExportSheet,
    reportTitle: string,
    reportCategory: string,
): Promise<void> {
    const XLSX = await import('xlsx');
    const data = [...sheet.rows];
    if (sheet.footerRow) {
        data.push(sheet.footerRow);
    }

    const worksheet = XLSX.utils.aoa_to_sheet([sheet.headers, ...data]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
    const output = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([output], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    await downloadFile(buildExportFilename(reportCategory, reportTitle, 'xlsx'), blob);
}

async function exportPdf(
    sheet: ReportExportSheet,
    reportTitle: string,
    reportCategory: string,
): Promise<void> {
    const { default: jsPDF } = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');

    const orientation = sheet.headers.length > 6 ? 'landscape' : 'portrait';
    const doc = new jsPDF({ orientation, unit: 'pt', format: 'letter' });

    doc.setFontSize(14);
    doc.text(reportTitle, 40, 40);

    autoTable(doc, {
        startY: 56,
        head: [sheet.headers],
        body: sheet.rows,
        foot: sheet.footerRow ? [sheet.footerRow] : undefined,
        styles: {
            fontSize: 8,
            cellPadding: 4,
            overflow: 'linebreak',
        },
        headStyles: {
            fillColor: [37, 99, 235],
            textColor: 255,
        },
        footStyles: {
            fillColor: [241, 245, 249],
            textColor: 20,
            fontStyle: 'bold',
        },
        margin: { left: 40, right: 40 },
    });

    doc.save(buildExportFilename(reportCategory, reportTitle, 'pdf'));
}

export async function exportReport<TRow extends Record<string, unknown>>(
    format: ReportExportFormat,
    options: ReportExportOptions<TRow>,
): Promise<void> {
    const sheet = buildReportExportSheet(options);

    switch (format) {
        case 'csv':
            await exportCsv(sheet, options.reportTitle, options.reportCategory);
            return;
        case 'xlsx':
            await exportXlsx(sheet, options.reportTitle, options.reportCategory);
            return;
        case 'pdf':
            await exportPdf(sheet, options.reportTitle, options.reportCategory);
            return;
        default: {
            const _exhaustive: never = format;
            throw new Error(`Unsupported export format: ${_exhaustive}`);
        }
    }
}
